"use client";

import { useEffect, useMemo, useState } from "react";
import { OnboardingProgressBar } from "./OnboardingProgressBar";
import { ConnectSocialStep } from "./steps/ConnectSocialStep";
import { CreateAccountStep } from "./steps/CreateAccountStep";
import { LinkCEXStep } from "./steps/LinkCEXStep";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { useHubUser, useDevice } from "@/hooks";
import { useOnboarding, hasExchangeAccounts } from "@/hooks/onboarding";
import { cn } from "@/lib/utils";
import { ONBOARDING_STEPS, ONBOARDING_STEP_ORDER, OnboardingStepType } from "@/store/onboardingStore";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingModal = ({ open, onClose }: OnboardingModalProps) => {
  const { currentStep, goToNextStep, goToPreviousStep, canGoPrevious, reset, isOnboardingCompleted, handleSkipStep } =
    useOnboarding();

  const { authenticated, hubUser } = useHubUser();
  const { isMobile } = useDevice();

  const [prevCurrentStep, setPrevCurrentStep] = useState<OnboardingStepType>(currentStep);

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    if (canGoPrevious()) {
      goToPreviousStep();
    }
  };

  const isFirstStep = currentStep === ONBOARDING_STEPS.CREATE_ACCOUNT;
  const isCEXStep = currentStep === ONBOARDING_STEPS.LINK_CEX;
  const isLastStep = currentStep === ONBOARDING_STEPS.CONNECT_SOCIAL;

  // Check account connection status
  const userHasExchangeAccounts = hasExchangeAccounts(hubUser);

  // Show navigation buttons if not on first step, OR if on first step but wallet is connected
  const shouldShowNavigation = !isFirstStep || (authenticated && hubUser);

  const containerClasses = cn(
    "flex flex-col gap-6",
    isMobile ? "pt-12 pb-6 px-4 min-h-[92vh]" : "p-8",
    "sm:h-screen md:h-screen lg:h-screen sm:max-h-[100vh]",
    "sm:w-[608px] sm:ml-auto sm:mr-0 sm:rounded-none sm:border-l sm:border-cede-border-container",
    "z-modal-high",
  );

  const handleNext = () => {
    if ((isFirstStep && authenticated && hubUser) || (isCEXStep && userHasExchangeAccounts)) {
      goToNextStep();
      return;
    }

    handleSkipStep(currentStep);
  };

  const nextButtonText = useMemo(() => {
    if (isFirstStep && !authenticated) return "Continue";
    if (isFirstStep && authenticated && hubUser) return "Continue";
    if (isCEXStep && userHasExchangeAccounts) return "Next";
    if (isLastStep) return "Finish";
    return "Skip";
  }, [isFirstStep, authenticated, hubUser, isCEXStep, userHasExchangeAccounts, isLastStep]);

  // Track step changes to determine slide direction
  useEffect(() => {
    if (currentStep !== prevCurrentStep) {
      setPrevCurrentStep(currentStep);
    }
  }, [currentStep, prevCurrentStep]);

  // Reset onboarding state when modal closes
  useEffect(() => {
    if (!open && !isOnboardingCompleted) {
      reset();
    }
  }, [open, isOnboardingCompleted, reset]);

  // Calculate slide position for each step
  const getSlidePosition = (step: OnboardingStepType) => {
    const currentIndex = ONBOARDING_STEP_ORDER.indexOf(currentStep);
    const stepIndex = ONBOARDING_STEP_ORDER.indexOf(step);

    if (stepIndex === currentIndex) return 0; // Current step is centered
    if (stepIndex < currentIndex) return -100; // Previous steps are to the left
    return 100; // Future steps are to the right
  };

  // Render individual step with slide animation
  const renderStep = (step: OnboardingStepType) => {
    const isActive = step === currentStep;
    const slidePosition = getSlidePosition(step);

    const stepContent = () => {
      switch (step) {
        case ONBOARDING_STEPS.CREATE_ACCOUNT:
          return <CreateAccountStep />;
        case ONBOARDING_STEPS.LINK_CEX:
          return <LinkCEXStep />;
        case ONBOARDING_STEPS.CONNECT_SOCIAL:
          return <ConnectSocialStep />;
        default:
          return null;
      }
    };

    return (
      <div
        key={step}
        className="absolute inset-0 flex items-center justify-center transition-all duration-300 ease-in-out"
        style={{
          opacity: isActive ? 1 : 0,
          transform: `translateX(${slidePosition}%)`,
        }}
        {...(!isActive && { inert: true })}
      >
        {stepContent()}
      </div>
    );
  };

  return (
    <PortalModal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      showCloseButton={isMobile}
      closeButtonClassName={isMobile ? "sm:top-2 sm:right-2" : undefined}
      className={containerClasses}
      style={!isMobile ? { borderRadius: "16px 0 0 16px" } : undefined}
    >
      {/* Progress bar */}
      <div>
        <OnboardingProgressBar />
      </div>

      {/* Steps container with slide animations */}
      <div className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
        {ONBOARDING_STEP_ORDER.map((step) => renderStep(step))}
      </div>

      {/* Navigation buttons - show based on shouldShowNavigation logic */}
      {shouldShowNavigation && (
        <div
          className={cn(
            "flex items-center gap-4",
            isFirstStep && authenticated && hubUser ? "justify-end" : "justify-between",
          )}
        >
          {/* Back button - only show if not on first step */}
          {!isFirstStep && (
            <Button variant="secondary" size="medium" onClick={handleBack} className="gap-2">
              <ArrowLeft size={16} />
              Back
            </Button>
          )}

          {/* Continue/Skip/Next button */}
          <Button variant="primary" size="medium" onClick={handleNext} className="gap-2">
            {nextButtonText}
            <ArrowRight size={16} />
          </Button>
        </div>
      )}
    </PortalModal>
  );
};
